import {debugWarn} from './debug';
import type {CensorMeJwtClaims} from '../backend-types/signup-api';

/**
 * CensorMe's custom claims, read off the Supabase access token.
 *
 * Only worth reading for `censor_me_password_set`, and only as the *early* answer: a freshly
 * provisioned account arrives on its login link before any profile request has landed, and the
 * set-password screen has to be in front of it immediately. `GET /rest/v1/user/profile` is the
 * authoritative source once it answers.
 *
 * Claims are stamped when the token is issued, so this **goes stale**: after `POST
 * /rest/v1/user/password` the claim stays false until the session refreshes. Trust that response,
 * not this. (These claims were silently absent from every token issued before the backend fixed it,
 * so treat a missing claim as "unknown", never as false.)
 */
export function decodeCensorMeClaims(accessToken: string | null | undefined): Partial<CensorMeJwtClaims> | null {
  if (!accessToken) {
    return null;
  }

  const payload = accessToken.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    // base64url → base64, then pad. atob rejects the URL-safe alphabet.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Partial<CensorMeJwtClaims>;
  } catch (error) {
    debugWarn('[jwtClaims] Could not decode the access token:', error);
    return null;
  }
}
